import React, { createContext, useState, useEffect, useContext } from "react";
import { getCurrentTheme, themePalette } from "../styles/theme";
import { CssBaseline, ThemeProvider as MuiThemeProvider } from "@mui/material";

export const ThemeContext = createContext();

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  const [theme, setTheme] = useState(getCurrentTheme());
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize theme and check dark mode
  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setIsDarkMode(isDark);
    setTheme(getCurrentTheme());
    
    // Listen for theme changes (when user toggles dark mode)
    const handleThemeChange = () => {
      const newIsDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(newIsDark);
      setTheme(getCurrentTheme());
    };

    window.addEventListener('themechange', handleThemeChange);
    
    // Observe class changes on html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });

    return () => {
      window.removeEventListener('themechange', handleThemeChange);
      observer.disconnect();
    };
  }, []);

  const toggleTheme = () => {
    const isDark = document.documentElement.classList.contains('dark');
    if (isDark) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
    // Dispatch event to notify theme change
    window.dispatchEvent(new Event('themechange'));
  };

  const setLightTheme = () => {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
    window.dispatchEvent(new Event('themechange'));
  };

  const setDarkTheme = () => {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
    window.dispatchEvent(new Event('themechange'));
  };

  const values = { 
    theme, 
    isDarkMode, 
    toggleTheme, 
    setLightTheme, 
    setDarkTheme 
  };

  return (
    <ThemeContext.Provider value={values}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};